/**
 * Marketing email capture.
 *
 * Every place on the marketing site where a visitor can enter an email address
 * routes through `captureLead`, which persists the submission to the
 * `marketing_submissions` table in Supabase (see
 * `supabase/migrations/20260710120000_create_marketing_submissions.sql`).
 *
 * Rows are segmented so the team can tell, for example, someone who signed up
 * for product notifications apart from someone reaching out through a contact
 * form. Marketing visitors are unauthenticated, so inserts go through the anon
 * key and are allowed by a public INSERT RLS policy; reads are admin-only.
 *
 * The call is intentionally forgiving: when Supabase is not configured (e.g.
 * local dev without `.env.local`) it resolves without error so the form's
 * success UI still works, mirroring the fire-and-forget style used by
 * `auditLog.js`.
 */

import { supabase, isSupabaseConfigured } from "../lib/supabaseClient";
import { captureEvent } from "../lib/posthog";

// PostHog conversion events.
//
// Every marketing capture point routes through `captureLead`, so instrumenting
// here — rather than at each of the eleven call sites — means a new form is
// counted the moment it calls this function, and the two can never drift. The
// `segment` and `source` properties are what distinguish a contact enquiry from
// a product-notification signup, so a single Action (or web-analytics
// conversion goal) filtered on `segment` covers each funnel without more code.
//
// Fired only after the row is actually persisted: the form's success UI also
// shows when Supabase isn't configured at all (local dev), and counting those
// as conversions would inflate the numbers with submissions nobody received.
const LEAD_EVENT = "lead_submitted";

// The rejected submissions, most often the fail-closed Turnstile path — which
// is otherwise invisible, since the visitor just sees "please try again". A
// broken or undeployed submit-lead function would look like a drop in traffic
// rather than the outage it is.
const LEAD_FAILED_EVENT = "lead_submission_failed";

/**
 * Report a submission outcome to PostHog.
 *
 * Deliberately carries no email, name or payload. The address is already in
 * `marketing_submissions` and the CRM; copying it into a third-party analytics
 * store adds visitor PII for no conversion-reporting benefit, since PostHog
 * attributes the conversion from its own campaign/session properties.
 */
function reportSubmission(row, { failureReason } = {}) {
  captureEvent(failureReason ? LEAD_FAILED_EVENT : LEAD_EVENT, {
    segment: row.segment,
    source: row.source,
    page_path: row.page_path,
    ...(failureReason ? { reason: failureReason } : null),
  });
}

// Keep in sync with the CHECK constraint / RLS policy in the migration.
export const LEAD_SEGMENTS = {
  PRODUCT_NOTIFICATION: "product_notification",
  CONTACT: "contact",
  DEMO_REQUEST: "demo_request",
  ENROLLMENT: "enrollment",
  ORDER: "order",
  SUPPORT: "support",
  TRIAL_REQUEST: "trial_request",
};

const TABLE = "marketing_submissions";

// Edge function that verifies the Cloudflare Turnstile token (secret key) and
// performs the insert with the service role. See
// `supabase/functions/submit-lead/index.ts`.
const SUBMIT_FUNCTION = "submit-lead";

/**
 * Persist a marketing email submission.
 *
 * When a Turnstile `token` is supplied, the submission MUST go through the
 * `submit-lead` edge function, which verifies the token with Cloudflare before
 * writing the row with the service role. If that call fails for any reason the
 * submission fails too — we never fall back to an unverified direct insert, as
 * that would persist a row the widget never validated. Tokenless calls are
 * trusted (authenticated in-app forms, or the enrollment flow after a
 * captcha-verified signUp) and insert directly.
 *
 * @param {Object} params
 * @param {string} params.email    Required. The visitor's email address.
 * @param {string} params.segment  Required. One of LEAD_SEGMENTS.
 * @param {string} [params.source] Which form/page produced the row.
 * @param {string} [params.name]   Display name, when the form collected one.
 * @param {Object} [params.payload] Full form detail to keep for follow-up.
 * @param {string} [params.token]  Cloudflare Turnstile token from the widget.
 * @returns {Promise<{ error: (Error|null) }>}
 */
export async function captureLead({
  email,
  segment,
  source,
  name,
  payload,
  token,
} = {}) {
  const normalizedEmail = (email || "").trim().toLowerCase();

  if (!normalizedEmail) {
    return { error: new Error("captureLead: email is required") };
  }

  if (!isSupabaseConfigured) {
    // No backend configured (e.g. local dev). Don't block the form's success
    // state; just note it so the missing wiring is easy to spot in the console.
    // eslint-disable-next-line no-console
    console.warn(
      "[Qentoros] Lead capture skipped: Supabase is not configured. " +
        "Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to persist submissions."
    );
    return { error: null };
  }

  const row = {
    email: normalizedEmail,
    segment,
    source: source || null,
    name: name ? String(name).trim() : null,
    page_path: typeof window !== "undefined" ? window.location.pathname : null,
    payload: payload || {},
  };

  // Public/anonymous forms pass a Turnstile `token`. For these, server-side
  // verification is MANDATORY: the submission goes through the submit-lead edge
  // function, which validates the token with Cloudflare and writes the row with
  // the service role. We never fall back to a direct insert here — an
  // unverified row would defeat the bot protection, and the RLS policy blocks
  // anon direct inserts for these segments anyway (see the
  // `restrict_marketing_submissions_insert` migration).
  if (token) {
    // `supabase.functions.invoke` reports an unreachable/undeployed function or
    // a 5xx as a *returned* error object — it does NOT throw — so capture the
    // failure from both the returned value and any thrown exception.
    let fnError = null;
    try {
      const { error } = await supabase.functions.invoke(SUBMIT_FUNCTION, {
        body: { ...row, token },
      });
      if (!error) {
        reportSubmission(row);
        return { error: null };
      }
      fnError = error;
    } catch (err) {
      fnError = err;
    }

    // Fail closed. Whether the function rejected the token (4xx) or was
    // unavailable / misconfigured (5xx, network error, not deployed), do NOT
    // write the row unverified — surface the error so the form can prompt a
    // retry.
    // eslint-disable-next-line no-console
    console.warn(
      "submit-lead did not accept the submission; not falling back to a direct insert",
      fnError?.message || fnError
    );
    // `name` is a supabase-js error class (FunctionsHttpError when the function
    // rejected the token, FunctionsFetchError when it couldn't be reached) —
    // enough to tell a captcha failure from an outage, and free of visitor data.
    reportSubmission(row, {
      failureReason: fnError?.name || "verification_unavailable",
    });
    return { error: fnError || new Error("Lead verification unavailable") };
  }

  // No token: a trusted caller — an authenticated in-app form (order, support,
  // trial_request), or the enrollment form after a captcha-verified signUp.
  // These insert directly; RLS permits the authenticated and enrollment paths.
  try {
    const { error } = await supabase.from(TABLE).insert(row);

    if (error) {
      // eslint-disable-next-line no-console
      console.warn("Lead capture insert failed", error.message || error);
    }

    reportSubmission(row, {
      failureReason: error ? error.code || "insert_failed" : undefined,
    });
    return { error: error || null };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("Lead capture insert threw", err);
    reportSubmission(row, { failureReason: "insert_threw" });
    return { error: err };
  }
}
